import { COVERAGE_REASON_LABELS } from '@tinypro-audit/contracts';
import type { ReviewTopic } from './project-review-model';

export function preSiteReviewSummary(
  reviews: ReviewTopic[],
  assessmentCount: number,
) {
  if (!reviews.length)
    return {
      label: assessmentCount ? 'Review available' : 'Awaiting review',
      color: 'gray',
      description: assessmentCount
        ? 'An existing pre-site review is available. Order coverage will be checked when the ordering analysis completes.'
        : 'Pre-site review availability and order coverage will appear when the ordering analysis completes.',
    };
  const pending = reviews.filter(
    (review) =>
      !['covered', 'not_applicable'].includes(
        review.order.result!.measurement.state,
      ),
  );
  if (
    pending.length === reviews.length &&
    pending.every(
      (review) =>
        review.order.result!.measurement.state === 'assessment_unavailable',
    )
  )
    return {
      label: 'Review not found',
      color: 'yellow',
      description: assessmentCount
        ? 'No applicable pre-site review is linked to these orders, so order coverage cannot be verified.'
        : 'No completed pre-site review is linked to this project, so we cannot verify the order against it.',
    };
  if (pending.length) {
    const measurement = pending[0].order.result!.measurement;
    const reason = measurement.reason_codes?.[0];
    return {
      label:
        reviews.length === 1
          ? reason
            ? COVERAGE_REASON_LABELS[reason]
            : 'Order coverage unverified'
          : `${pending.length} ${pending.length === 1 ? 'order needs' : 'orders need'} review`,
      color: 'yellow',
      description:
        'Check whether the existing pre-site review covers the ordered configuration and current form.',
    };
  }
  const covered = reviews.filter(
    (review) => review.order.result!.measurement.state === 'covered',
  );
  return {
    label: covered.length ? 'Review applies' : 'Not required for these orders',
    color: covered.length ? 'teal' : 'gray',
    description: covered.length
      ? `The existing pre-site review covers ${covered.length === 1 ? 'the ordered configuration' : `${covered.length} ordered configurations`}. Original findings and grades remain available below.`
      : 'The ordering analysis marked pre-site review as not applicable to these orders.',
  };
}

export function preSiteNextAction(review: ReviewTopic): string | null {
  const measurement = review.order.result!.measurement;
  if (['covered', 'not_applicable'].includes(measurement.state)) return null;
  if (measurement.state === 'assessment_unavailable')
    return 'Locate the existing pre-site form review for this project. If none exists, arrange one through the established pre-site review process.';
  const reasons = measurement.reason_codes ?? [];
  if (
    reasons.some((reason) =>
      ['configuration_changed', 'measurements_changed'].includes(reason),
    )
  )
    return 'Arrange a new pre-site form review, then check that the updated review applies to this order.';
  if (reasons.includes('freshness_unverified'))
    return 'Confirm that the current pre-site form matches the reviewed revision. If this cannot be established, arrange a new pre-site review.';
  return 'Arrange a new pre-site form review, then check that the updated review applies to this order.';
}
